import { useContext, useEffect, useRef } from 'react';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import EditElementModal from '@/components/Custom/Modal/EditElementModal';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import { ElementType } from '@/entities/ia';
import { showModal } from '@/utils/modal.utils';
import { getLabelByValue, getColorByValue } from '@/utils/ia.utils';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import { TFunction } from 'i18next';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { twMerge } from 'tailwind-merge';
import { NotFound } from '@/components/Message/NotFound';
import { focusButton } from '@/utils/button.utils';

interface ListElementCarProps {
  t?: TFunction<undefined>;
  isLoading?: boolean;
  listElement: ElementType[];
  showElement: boolean;
  showOnlyEquipments?: boolean;
  showOnlyDamages?: boolean;
  showOnlyDetections?: boolean;
  equipmentClassId?: number;
  onConfirmModal: () => void;
  onEdit: (value: string) => void;
}

const ListElementCar = ({
  t,
  isLoading,
  listElement,
  showElement,
  showOnlyEquipments = false,
  showOnlyDamages = false,
  showOnlyDetections = false,
  equipmentClassId,
  onConfirmModal,
  onEdit,
}: ListElementCarProps) => {
  const modifyModalValidateButtonRef = useRef<HTMLButtonElement | null>(null);

  const { setCurrentElement, currentElement, currentElCanvas } = useContext(
    EngineCorrectionContext,
  );
  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { allDamagesSelectOptions } = useGetModerationDamages();

  useEffect(() => {
    const idElement = `${currentElement['elNumber']}_${currentElement['index']}`;
    const element = document.getElementById(idElement);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentElCanvas]);

  return (
    <div>
      {!isLoading && Boolean(listElement?.length) && (
        <div className="text-2sm text-primary font-medium text-center border border-dashed border-gray-400 px-2.75 py-[7px] mb-2.5">
          {showElement
            ? `${t('common:totalEquipments')} : ${listElement?.length || 0}`
            : `${t('common:totalDamages')} : ${listElement?.length || 0}`}
        </div>
      )}
      <div className="h-96 overflow-y-auto">
        {isLoading ? (
          <div className="text-2sm text-primary font-medium text-center border border-dashed border-gray-400 px-2.75 py-[7px] mb-2.5">{`${t(
            'common:loading',
          )} ...`}</div>
        ) : listElement?.length === 0 ? (
          <NotFound
            containerClassName="mt-4 gap-1.75"
            textClassName="text-sm"
            iconClassName="-mt-0.5 text-[21px]"
            text={
              showOnlyEquipments
                ? showOnlyDetections
                  ? t('noEquipmentDetected')
                  : t('noEquipmentModerated')
                : showOnlyDamages
                  ? showOnlyDetections
                    ? t('noDamageDetected')
                    : t('noDamageModerated')
                  : showElement
                    ? t('noDamageDetectedOrModerated')
                    : t('noEquipmentDetectedOrModerated')
            }
          />
        ) : (
          <>
            {listElement?.map((element, index) => (
              <div
                key={showElement ? `element-${index}` : `dmg-${index}`}
                id={`${element.elNumber}_${index}`}
                className={twMerge(
                  'pl-3 py-1.5 rounded-md border border-transparent hover:cursor-pointer',
                  currentElement['elNumber'] === element.elNumber &&
                    currentElement['index'] === index &&
                    'bg-purple-DDD6FF/35 border-purple-DDD6FF/90',
                )}
                onMouseOver={() => {
                  setCurrentElement({
                    index,
                    elNumber: element.elNumber,
                  });
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex flex-1 gap-2">
                    <div
                      className="mt-[3.7px] w-3 min-w-3 h-3 min-h-3 border border-purple-5433FF/20 rounded-full"
                      style={{
                        backgroundColor: getColorByValue(
                          element?.elNumber?.toString(),
                          showElement,
                          equipmentsSelectOptions,
                          allDamagesSelectOptions,
                        ),
                      }}
                    />
                    <div className="text-sm font-medium break-words">
                      {getLabelByValue(
                        t,
                        element?.elNumber?.toString(),
                        showElement,
                        equipmentsSelectOptions,
                        allDamagesSelectOptions,
                      )}
                    </div>
                  </div>
                  <TableMenu>
                    {/* Check if equipmentClassId number is NOT provided */}
                    {isNaN(equipmentClassId) && (
                      <SubMenuItem
                        text={t('common:modify')}
                        leftIconClassName="ki-notepad-edit !text-[16px]"
                        onClick={() => {
                          showModal('#edit-element-modal');
                          focusButton(modifyModalValidateButtonRef);
                        }}
                      />
                    )}
                    <SubMenuItem
                      text={t('common:remove')}
                      leftIconClassName="ki-trash !text-[19px]"
                      textClassName="line-clamp-1"
                      onClick={() => {
                        showModal('#confirmation-modal');
                        setCurrentElement({
                          index,
                          elNumber: element.elNumber,
                        });
                      }}
                    />
                  </TableMenu>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      <ConfirmationModal
        id="confirmation-modal"
        title={t('common:confirmation')}
        description={`${t('common:areYouSureToDeleteThisElement')} ?`}
        onConfirmLabel={t('common:delete')}
        onConfirm={onConfirmModal}
      />
      <EditElementModal
        t={t}
        id="edit-element-modal"
        equipmentClassId={equipmentClassId}
        confirmLabel={t('common:modify')}
        defaultValue={currentElement['elNumber']?.toString()}
        secondButtonRef={modifyModalValidateButtonRef}
        onConfirm={onEdit}
        isElement={showElement ? true : false}
      />
    </div>
  );
};

export default ListElementCar;
